import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import Table from '@/common/components/Table'
import { useCardWithSecureInformation } from '@/common/hooks/queries/useCardWithSecureInformation'
import { useConsumptionsByStatus } from '@/common/hooks/queries/useConsumptionsByStatus'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { FailedConsumptionData } from '@/common/types/client/failedConsumption.type'
import { ClientFailedConsumptionsTransformer } from '@/features/privateCompanies/services/clientFailedConsumptionsTransformer'
import { ClientFailedConsumptionTableItem } from '@/features/privateCompanies/types/clientFailedConsumption.type'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { defaultColumns } from './columns'
import Filters from './components/Filters'
import LoadingSpinner from '@/common/components/LoadingSpinner'

const FailedConsumptionList = () => {
  const { id: companyId } = useParams()
  const navigate = useNavigate()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { filterParams, setFilterParams } = useFilterParams()

  const { mutate: cardData, isPending: cardDataIsPending } = useCardWithSecureInformation({})

  const handleRedirectToConsumption = ({
    cardId,
    establishment,
    product,
  }: {
    cardId: string
    establishment: { id: string; label: string }
    product: { id: string; displayName: string }
  }) => {
    cardData(cardId, {
      onSuccess: (response) => {
        const cardNumber = response?.data?.data?.number

        if (cardNumber) {
          navigate(
            `/card-current-limit?cardIdentifier=${cardNumber}&establishmentId=${establishment.id}&establishmentLabel=${establishment.label}&productId=${product.id}&productDisplayName=${product.displayName}`,
          )
        }
      },
      onError: (error) => {
        setMessages(error.response?.data.errors.base || ['Erro ao buscar dados do cartão.'])
      },
    })
  }

  const {
    data: companyBillingEvents,
    meta,
    isPending: isPendingCompanyBillingEvents,
    isFetching: isFetchingCompanyBillingEvents,
    error: companyBillingEventsError = {} as any,
    refetch: refetchCompanyBillingEvents,
  } = useConsumptionsByStatus({
    filters: {
      status: 'failed',
      page: filterParams.page || 1,
      clientCompanyId: companyId || '',
      clientBranchId: filterParams.branch?.value,
      establishmentBranchId: filterParams.establishmentCompany?.value,
    },
    mapper: Mapper.mapWithTransformer<FailedConsumptionData[], ClientFailedConsumptionTableItem[]>({
      transformer: ClientFailedConsumptionsTransformer.tableTransformer({
        redirectToConsumption: handleRedirectToConsumption,
        disableActions: cardDataIsPending,
      }),
    }),
  })

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-192px)]"
      statusCode={companyBillingEventsError?.status}
      refetchFunction={refetchCompanyBillingEvents}
    >
      <div className="flex flex-col gap-6">
        <div className='flex items-center justify-between'>
          <Filters companyId={companyId} />
          <LoadingSpinner isLoading={isFetchingCompanyBillingEvents} />
        </div>

        <Table
          className="max-h-[calc(100vh-248px)]"
          columns={defaultColumns}
          isLoadingData={isPendingCompanyBillingEvents}
          page={filterParams.page || 1}
          rows={companyBillingEvents}
          totalPages={meta.total_pages}
          totalCount={meta.total}
          hasPagination
          rowEmptyValue="--"
          setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
        />
      </div>

      {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
    </ErrorMessageWrapper>
  )
}

export default FailedConsumptionList
